import { useMemo } from 'react'

import { DashCard } from './DashboardCards'
import { ageBuckets } from '../../../lib/dashboard-insights'

import type { GisIssue } from '../../../api/types'
import type { BoardShortcuts } from '../../../hooks/useBoardShortcuts'

// Urgency ramp: fresh (green) → aged (red). The longer an issue has been pending, the worse.
const BAND_COLOR: Record<string, string> = {
  w1: '#10b981',
  w2: '#84cc16',
  w3: '#eab308',
  w4: '#f97316',
  m1: '#dc2626',
}

interface IssueAgingCardProps {
  /** Open issues in the current scope. */
  openIssues: GisIssue[]
  /** Cold-load flag (keepPreviousData keeps stale data during scope switches). */
  pending: boolean
  /** Currently-selected age band keys (the dashboard filter). */
  selected: readonly string[]
  /** Toggle a band in/out of the dashboard filter. */
  onToggle: (key: string) => void
  go: BoardShortcuts
}

/**
 * "Issues by age" — the OPEN backlog as a composition: one stacked distribution strip split by how
 * long each issue has been pending (0–1 week … over a month) on a green→red urgency ramp, over a
 * legend that names each band with its count + share. The bands are **multi-select filters**:
 * clicking one toggles it into the dashboard's age filter (which recomputes the KPI cards); the
 * filter bar above applies it to the Issues board.
 */
export function IssueAgingCard({ openIssues, pending, selected, onToggle, go }: IssueAgingCardProps) {
  const buckets = useMemo(() => ageBuckets(openIssues), [openIssues])
  const total = openIssues.length
  const overMonth = buckets[buckets.length - 1]?.count ?? 0
  const hasSelection = selected.length > 0

  return (
    <DashCard
      title="Issues by age"
      caption="click a band to filter"
      onOpen={() => go.goIssues()}
      openLabel="Open Issues board"
    >
      {pending ? (
        <div className="h-[150px] animate-pulse rounded-[10px] bg-gis-track-2" aria-hidden />
      ) : total === 0 ? (
        <div className="py-8 text-center text-[11.5px] text-gis-soft">No open issues in this selection.</div>
      ) : (
        <>
          {/* One stacked strip — each segment's width is that band's share of the open backlog. */}
          <div className="flex h-[18px] w-full overflow-hidden rounded-full bg-gis-track-2">
            {buckets.map((b) =>
              b.count > 0 ? (
                <span
                  key={b.key}
                  title={`${b.label}: ${b.count}`}
                  style={{
                    width: `${(b.count / total) * 100}%`,
                    background: BAND_COLOR[b.key] ?? '#94a3b8',
                    opacity: !hasSelection || selected.includes(b.key) ? 1 : 0.28,
                  }}
                  className="h-full transition-[width,opacity] duration-[500ms] ease-[cubic-bezier(.2,.7,.3,1)]"
                />
              ) : null,
            )}
          </div>

          {/* Legend — click a row to toggle that band into the filter. */}
          <div className="mt-3 flex flex-col gap-[5px]">
            {buckets.map((b) => {
              const pct = total > 0 ? Math.round((b.count / total) * 100) : 0
              const on = selected.includes(b.key)
              return (
                <button
                  key={b.key}
                  type="button"
                  onClick={() => onToggle(b.key)}
                  aria-pressed={on}
                  title={on ? `Remove ${b.label} from the filter` : `Add ${b.label} to the filter`}
                  className={`-mx-1 grid cursor-pointer grid-cols-[minmax(0,1fr)_auto] items-center gap-3 rounded-[8px] px-1 py-[3px] text-left transition-colors focus-visible:ring-2 focus-visible:ring-blue-500/60 ${on ? 'bg-[#eef3fb]' : 'hover:bg-[#f5f7fa]'}`}
                >
                  <span className="flex min-w-0 items-center gap-2">
                    <span
                      className={`h-[10px] w-[10px] flex-none rounded-[3px] ring-2 ${on ? 'ring-gis-navy/40' : 'ring-transparent'}`}
                      style={{ background: BAND_COLOR[b.key] ?? '#94a3b8' }}
                    />
                    <span className={`truncate text-[12px] font-semibold ${on ? 'text-gis-navy' : 'text-gis-ink-2'}`}>
                      {b.label}
                    </span>
                    {b.critical > 0 && (
                      <span className="flex-none text-[10px] font-semibold text-gis-red">{b.critical} critical</span>
                    )}
                  </span>
                  <span className="flex items-center gap-2 tabular-nums">
                    <span className="min-w-[24px] text-right text-[12.5px] font-bold text-gis-ink">{b.count}</span>
                    <span className="min-w-[34px] text-right text-[10.5px] text-gis-muted">{pct}%</span>
                  </span>
                </button>
              )
            })}
          </div>

          <p className="mt-2 border-t border-gis-line-2 pt-2 text-[10.5px] text-gis-soft">
            {hasSelection ? (
              <>
                <b className="text-gis-navy">{selected.length}</b> band{selected.length === 1 ? '' : 's'} selected — apply
                from the filter bar above.
              </>
            ) : overMonth > 0 ? (
              <>
                <span className="font-bold text-gis-red">{overMonth}</span> open issue
                {overMonth === 1 ? '' : 's'} pending over a month.
              </>
            ) : (
              <>Nothing pending beyond a month — the backlog is fresh.</>
            )}
          </p>
        </>
      )}
    </DashCard>
  )
}
